import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { SessionService } from 'account-hybrid/core/authentication';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import * as ContestsListActions from '../../../../account-v2/src/app/features/advocacy/group/audiences/actions';
import { AdvocacyNgReduxFacade } from '../store/advocacy-ngredux.facade';

/**
 * Native-Angular port of the AngularJS `ftContestsList` directive
 * (account-v2/.../advocacy/group/audiences/contestsList.component.ts).
 *
 * Shared by Advocacy Contests (Upcoming/Past sub-tabs) AND the upcoming
 * Audiences port — so it lives in its own folder with its own component,
 * not buried inside either leaf. The AngularJS original switched
 * `templateUrl` based on the `select="upcoming|past"` attribute; this
 * port keeps a single template that `*ngIf`-branches on `select` instead.
 *
 * Data source: `PhotoContest.future(propertyUuid)` / `PhotoContest.past(propertyUuid)`
 * (AngularJS factory, registered in `account-v1/.../core/api/photoAdvocacy.js`).
 * Dispatched through the AngularJS NgRx store via `ContestsListActions.getContests`,
 * which returns the response payload as a promise → assigned to `contests`.
 *
 * Clicking a card navigates to the native `advocacy.contests.{details,winners}`
 * UIRouter state (same as `ContestsComponent.showContest`). The AngularJS
 * `ContestContainer` modal service was deleted in WS4 inc-6, so the old
 * `$injector.get('ContestContainer')` was a dangling throw — replaced here,
 * which also drops this component's last $injector bridge (WS5 Phase 3).
 */
@Component({
    selector: 'ft-contests-list',
    templateUrl: './contests-list.component.html',
    standalone: false
})
export class ContestsListComponent implements OnInit, OnDestroy {
  @Input() select!: 'upcoming' | 'past';
  /** Optional theme filter — matched against contest.theme. Mirrors v1's `theme: vm.theme` filter. */
  @Input() theme?: number;

  property: any;
  contests: any;
  isInitialized = false;

  private $ngRedux: any;

  constructor(
    private readonly spacesService: SpacesService,
    private readonly sessionService: SessionService,
    private readonly stateService: StateService,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
    private readonly advStore: AdvocacyNgReduxFacade,
  ) {}

  ngOnInit(): void {
    this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid()!);
    // WS2 strangle: advocacy state now in the native NgRx feature (facade).
    this.$ngRedux = this.advStore;

    this.$ngRedux
      .dispatch(ContestsListActions.getContests(this.property.UUID, this.select))
      .then((data: any) => {
        this.contests = data;
        this.isInitialized = true;
      });
  }

  ngOnDestroy(): void {
    // No $ngRedux.connect to dispose — getContests returns a one-shot promise.
  }

  /** v1: `vm.contests.item | filter:{theme: vm.theme}` — only applies when @Input theme is set. */
  get filteredItems(): any[] {
    const items = this.contests?.item ?? [];
    if (this.theme == null) return items;
    return items.filter((c: any) => c.theme === this.theme);
  }

  getContestBackgroundImage(contest: any): string {
    if (contest?.activityPhoto) {
      return buildAzureStoredPhotoUrl(contest.activityPhoto, undefined, this.environment.AzureCdnBaseUrl) as string;
    }
    return contest?.propertyBackgroundImageUrl ?? '';
  }

  showContest(contest: any, activeSelection: string): void {
    this.stateService.go(`advocacy.contests.${activeSelection}`, { contestUuid: contest.id });
  }
}
